/**
 * Content Service
 * Service for fetching and managing content from backend
 */
import isNumber from 'lodash/isNumber';
import { countQuestions, isPersistedContentId } from '@ai-content-hub/content-core';
import type { IEditorModel, IPlayerModel, IContentMetadata } from '@ai-content-hub/h5p-server';
import {
    getApiConfig,
    getH5pRequestHeaders,
    getSharedApiAuthHeaders,
    sharedApiClient,
    type PostFormDataOptions,
} from './client';

/** Aligns with backend Content.STATUS_* (content/models.py). */
export const ContentStatus = {
    DELETE: 0,
    IMPORTING: 1,
    DRAFT: 2,
    PUBLISHED: 3,
} as const;

export type ContentStatusValue = (typeof ContentStatus)[keyof typeof ContentStatus];

export type ContentStatusLabel =
    | 'delete'
    | 'importing'
    | 'draft'
    | 'published';

export const CONTENT_DIFFICULTY_VALUES = ['easy', 'medium', 'hard'] as const;
export type ContentDifficulty = (typeof CONTENT_DIFFICULTY_VALUES)[number];

export const ContentStatusLabels: Record<ContentStatusValue, ContentStatusLabel> = {
    [ContentStatus.DELETE]: 'delete',
    [ContentStatus.IMPORTING]: 'importing',
    [ContentStatus.DRAFT]: 'draft',
    [ContentStatus.PUBLISHED]: 'published',
};

/** API may return integer status or string label after StatusField serialization. */
export function getStatusLabel(status: ContentStatusValue | number | string): ContentStatusLabel {
    if (typeof status === 'string') {
        const lower = status.toLowerCase() as ContentStatusLabel;
        if (Object.values(ContentStatusLabels).includes(lower)) {
            return lower;
        }
        return ContentStatusLabels[getStatusValue(lower)];
    }
    if (isNumber(status) && status in ContentStatusLabels) {
        return ContentStatusLabels[status as ContentStatusValue];
    }
    return 'draft';
}

export function getStatusValue(label: string): ContentStatusValue {
    const entry = Object.entries(ContentStatusLabels).find(([, l]) => l === label.toLowerCase());
    return entry ? (Number(entry[0]) as ContentStatusValue) : ContentStatus.DRAFT;
}

/** Normalize list/detail status to integer for forms and filters. */
export function normalizeContentStatus(
    status: ContentStatusValue | number | string | undefined,
): ContentStatusValue {
    if (status === undefined || status === null) {
        return ContentStatus.DRAFT;
    }
    if (typeof status === 'number' && status in ContentStatusLabels) {
        return status as ContentStatusValue;
    }
    if (typeof status === 'string') {
        return getStatusValue(status);
    }
    return ContentStatus.DRAFT;
}

// Types
export interface ContentSource {
    id: string;
    name: string;
    code: string;
    description: string;
}

export interface ContentType {
    id: string;
    slug: string;
    name: string;
    description: string;
    default_config: Record<string, unknown> | null;
    library?: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    source?: ContentSource | null;
}

export interface AIContentTypeRef {
    id: string;
    slug: string;
    code: string;
    name: string;
    description: string;
    category: string;
    content_type_id: string;
    library: string;
    source?: ContentSource | null;
    is_active: boolean;
}

/** Tag summary embedded on content list/detail responses */
export interface ContentTagRef {
    id: string;
    code: string;
    name: string;
    tag_type_id: string;
}

/** Computed metadata stored on each content row */
export interface ContentMetadata {
    sub_question_count?: number;
    thumbnail?: string | null;
    author?: string | null;
}

export interface Content {
    id: string;
    tenant_id?: string;
    code: string;
    meta_id: string;
    title: string;
    content_type: ContentType;
    ai_content_type?: AIContentTypeRef | null;
    data: Record<string, unknown>;
    config: Record<string, unknown> | null;
    status: ContentStatusValue | ContentStatusLabel;
    status_label: string;
    tags?: ContentTagRef[];
    /** easy | medium | hard; null/omitted for legacy rows */
    difficulty?: ContentDifficulty | null;
    /** Computed metadata: sub_question_count, thumbnail, author */
    metadata?: ContentMetadata | null;
    created_at: string;
    updated_at: string;
}

export interface ContentListItem {
    id: string;
    code: string;
    meta_id: string;
    title: string;
    content_type: ContentType;
    ai_content_type?: AIContentTypeRef | null;
    status: ContentStatusValue | ContentStatusLabel;
    status_label: string;
    tags?: ContentTagRef[];
    difficulty?: ContentDifficulty | null;
    /** Computed metadata: sub_question_count, thumbnail, author */
    metadata?: ContentMetadata | null;
    created_at: string;
    updated_at: string;
}

export interface ContentListResponse {
    results: ContentListItem[];
    count: number;
    page: number;
    page_size: number;
    total_pages: number;
}

export interface ContentListParams {
    page?: number;
    page_size?: number;
    content_type?: string;
    status?: ContentStatusValue | string;
    search?: string;
    code?: string;
    tenant_id?: string;
    ordering?: string;
    /** Filter by created_at >= this date (ISO string) */
    created_after?: string;
    /** Filter by created_at <= this date (ISO string) */
    created_before?: string;
    /** Filter by updated_at >= this date (ISO string) */
    updated_after?: string;
    /** Filter by updated_at <= this date (ISO string) */
    updated_before?: string;
    /** Filter by difficulty (easy, medium, hard) */
    difficulty?: ContentDifficulty;
    /** Filter by tag UUIDs (AND/OR per backend contract) */
    tag_ids?: string[];
}

export interface CreateContentData {
    content_type_id: string;
    title: string;
    data: Record<string, unknown>;
    config?: Record<string, unknown> | null;
    status?: ContentStatusValue;
    /**
     * easy | medium | hard. API requires a value on create; when omitted,
     * `createContent()` sends `medium` so non–question-bank callers stay compatible.
     */
    difficulty?: ContentDifficulty;
    /** Tag UUIDs; optional. Replaces tags on create when set. */
    tag_ids?: string[];
    /** Metadata overrides (e.g. sub_question_count from FE) */
    metadata?: ContentMetadata | null;
    tenant_id?: string;
}

export interface UpdateContentData {
    content_type_id?: string;
    title?: string;
    data?: Record<string, unknown>;
    config?: Record<string, unknown> | null;
    status?: ContentStatusValue;
    difficulty?: ContentDifficulty | null;
    /** When present, replaces the content’s tags for the tenant. */
    tag_ids?: string[];
    /** Metadata overrides (e.g. sub_question_count from FE) */
    metadata?: ContentMetadata | null;
}

export interface CreateContentTypeData {
    slug: string;
    name: string;
    description?: string;
    default_config?: Record<string, unknown> | null;
    library?: string;
    is_active?: boolean;
    tenant_id?: string;
    is_master?: boolean;
    source_code?: string;
}

export interface ContentTypeListParams {
    is_active?: boolean;
    tenant_id?: string;
    source_code?: string;
    search?: string;
    page?: number;
    page_size?: number;
    ordering?: string;
}

export interface ContentTypeListResponse {
    results: ContentType[];
    count: number;
    page: number;
    page_size: number;
    total_pages: number;
}

export interface H5PContentData {
    library: string;
    params: unknown;
    metadata: IContentMetadata;
}

/** Essay grading request for AI scoring API */
export interface EssayGradeRequest {
    user_answer: string;
    question_type?: string;
    language?: string;
    question_content?: string;
}

/** Payload for POST .../speaking/grade (multipart). At least one of user_answer, audio_file, audio_url. */
export interface SpeakingGradePayload {
    question_type: string;
    question_content?: string;
    user_answer?: string;
    audio_url?: string;
    picture_description?: string;
    picture_image_url?: string;
    language?: 'vi' | 'en';
    /** Learner id forwarded as X-User-ID to the scoring API */
    user_id?: string;
    audio_file?: Blob | File;
    picture_file?: Blob | File;
}

export type SpeakingGradeResult = Record<string, unknown>;

export function buildSpeakingGradeFormData(payload: SpeakingGradePayload): FormData {
    const fd = new FormData();
    fd.append('question_type', payload.question_type);
    if (payload.question_content != null && payload.question_content !== '') {
        fd.append('question_content', payload.question_content);
    }
    if (payload.user_answer != null && payload.user_answer !== '') {
        fd.append('user_answer', payload.user_answer);
    }
    if (payload.audio_url != null && payload.audio_url !== '') {
        fd.append('audio_url', payload.audio_url);
    }
    if (payload.picture_description != null && payload.picture_description !== '') {
        fd.append('picture_description', payload.picture_description);
    }
    if (payload.picture_image_url != null && payload.picture_image_url !== '') {
        fd.append('picture_image_url', payload.picture_image_url);
    }
    if (payload.language != null) {
        fd.append('language', payload.language);
    }
    if (payload.user_id != null && payload.user_id !== '') {
        fd.append('user_id', payload.user_id);
    }
    if (payload.audio_file != null) {
        fd.append('audio_file', payload.audio_file);
    }
    if (payload.picture_file != null) {
        fd.append('picture_file', payload.picture_file);
    }
    return fd;
}

/**
 * EAA-2117: content types that `POST /content/draft/grade-job` can grade. The draft path
 * grades content that has not been saved yet (question-bank `/new` preview), so the BE has
 * no content row to resolve the type or tenant from and both travel in the request.
 */
export type DraftGradeContentType = 'essay' | 'speaking';

/** Accepted grade-job reply (202) — poll `generateService.pollJobStatus(job_id)`. */
export interface GradeJobStart {
    job_id: string;
    status: string;
}

/** EAA-2117: what `startEssayGradeJob` / `startSpeakingGradeJob` resolve with. */
export interface GradeJobStartResult extends GradeJobStart {
    /**
     * True when the job went to the draft endpoint. Learner session tokens are not
     * accepted there, so the caller must poll without one.
     */
    draft: boolean;
}

/** EAA-2117: options for the saved-vs-draft grade-job dispatch. */
export interface GradeJobDispatchOptions {
    /** Learner session JWT — saved content only; never sent on the draft path. */
    sessionToken?: string;
    /** Host tenant id — sent on the draft path when known. */
    tenantId?: string;
    /** Multipart options (speaking). */
    formOptions?: PostFormDataOptions;
}

/** Essay grading result from AI scoring API */
export interface EssayGradeResult {
    overall_score_percentage?: number;
    word_count?: number;
    criteria_scores?: Record<string, { score: number; max: number; description?: string }>;
    detailed_feedback?: {
        strengths?: string[];
        weaknesses?: string[];
        corrections?: string[];
    };
    suggested_revision?: string;
}

export interface ContentVersionItem {
    id: string;
    content_id: string;
    version: number;
    prompt: string;
    metadata: Record<string, unknown> | null;
    job_id: string | null;
    is_current: boolean;
    created_at: string;
}

/** Response when starting rollback (202 Accepted). Poll job status for completion. */
export interface RollbackResponse {
    job_id: string;
    status?: string;
    /** Set when job result is returned (e.g. from job status API) */
    content_id?: string;
    version?: number;
}

// Alias for backward compatibility
export type ContentTypeResponse = ContentType;

// Content Service Class
class ContentService {
    private basePath = '/content';

    // Content Type Methods
    async listContentTypes(params?: ContentTypeListParams): Promise<ContentTypeListResponse> {
        const queryParams = new URLSearchParams();
        if (params?.is_active !== undefined) {
            queryParams.append('active', String(params.is_active));
        }
        if (params?.tenant_id) {
            queryParams.append('tenant_id', params.tenant_id);
        }
        if (params?.source_code) {
            queryParams.append('source_code', params.source_code);
        }
        if (params?.search) {
            queryParams.append('search', params.search);
        }
        if (params?.page) {
            queryParams.append('page', String(params.page));
        }
        if (params?.page_size) {
            queryParams.append('page_size', String(params.page_size));
        }
        if (params?.ordering) {
            queryParams.append('ordering', params.ordering);
        }
        const queryString = queryParams.toString();
        const url = queryString
            ? `${this.basePath}/types/?${queryString}`
            : `${this.basePath}/types/`;

        const response = await sharedApiClient.get<ContentTypeListResponse>(url);
        return response.data;
    }

    async getContentType(id: string): Promise<ContentType> {
        const response = await sharedApiClient.get<ContentType>(`${this.basePath}/types/${id}`);
        return response.data;
    }

    async createContentType(data: CreateContentTypeData): Promise<ContentType> {
        const response = await sharedApiClient.post<ContentType>(`${this.basePath}/types/`, data);
        return response.data;
    }

    async updateContentType(
        id: string,
        data: Partial<CreateContentTypeData>
    ): Promise<ContentType> {
        const response = await sharedApiClient.put<ContentType>(
            `${this.basePath}/types/${id}`,
            data
        );
        return response.data;
    }

    async deleteContentType(id: string): Promise<void> {
        await sharedApiClient.delete(`${this.basePath}/types/${id}`);
    }

    // Content Methods
    async listContents(params: ContentListParams = {}): Promise<ContentListResponse> {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', String(params.page));
        if (params.page_size) queryParams.append('page_size', String(params.page_size));
        if (params.content_type) queryParams.append('content_type', params.content_type);
        if (isNumber(params.status)) queryParams.append('status', String(params.status));
        if (params.search) queryParams.append('search', params.search);
        if (params.code) queryParams.append('code', params.code);
        if (params.tenant_id) queryParams.append('tenant_id', params.tenant_id);
        if (params.ordering) queryParams.append('ordering', params.ordering);
        if (params.created_after) queryParams.append('created_after', params.created_after);
        if (params.created_before) queryParams.append('created_before', params.created_before);
        if (params.updated_after) queryParams.append('updated_after', params.updated_after);
        if (params.updated_before) queryParams.append('updated_before', params.updated_before);
        if (params.difficulty) queryParams.append('difficulty', params.difficulty);
        if (params.tag_ids?.length) {
            params.tag_ids.forEach((id) => queryParams.append('tag_ids', id));
        }

        const queryString = queryParams.toString();
        const url = queryString ? `${this.basePath}/?${queryString}` : this.basePath;

        const response = await sharedApiClient.get<ContentListResponse>(url);
        return response.data;
    }

    async getContent(id: string): Promise<Content> {
        const response = await sharedApiClient.get<Content>(`${this.basePath}/${id}`);
        return response.data;
    }

    async createContent(data: CreateContentData): Promise<Content> {
        const payload: CreateContentData = {
            ...data,
            difficulty: data.difficulty ?? 'medium',
        };
        const response = await sharedApiClient.post<Content>(`${this.basePath}/`, payload);
        return response.data;
    }

    async updateContent(id: string, data: UpdateContentData): Promise<Content> {
        const response = await sharedApiClient.put<Content>(`${this.basePath}/${id}`, data);
        return response.data;
    }

    async deleteContent(id: string): Promise<void> {
        await sharedApiClient.delete(`${this.basePath}/${id}`);
    }

    async duplicateContent(id: string): Promise<Content> {
        const response = await sharedApiClient.post<Content>(`${this.basePath}/${id}/duplicate`);
        return response.data;
    }

    /**
     * Bulk update multiple content items with the same field values.
     * @param contentIds - Array of content IDs to update
     * @param enabledFields - Fields to update: 'difficulty', 'status', 'tags'
     * @param data - Update data (difficulty, difficulty_mode, status, status_mode, tag_ids, tags_mode)
     * @returns Response with updated contents
     */
    async bulkUpdateContents(
        payload: Record<string, { difficulty?: string; status?: number; tagIds?: string[] }>
    ): Promise<ContentListResponse> {
        // EAA-1799: New per-content payload format
        // FE sends: { "content-id-1": { difficulty?: "easy", status?: 2, tagIds?: ["uuid"] }, ... }
        // BE accepts this format directly and applies per-content updates
        const response = await sharedApiClient.patch<ContentListResponse>(
            `${this.basePath}/bulk-update/`,
            payload
        );
        return response.data;
    }

    /**
     * Bulk delete multiple content items by ID.
     * DELETE /api/content/bulk-delete/ (body: { ids: string[] })
     */
    async bulkDeleteContents(ids: string[]): Promise<{ deleted: number; not_found: string[] }> {
        // WHY: sharedApiClient.delete() does not support a request body.
        // The BE endpoint reads ids from request.data (DELETE with JSON body).
        // Use a raw fetch with auth headers from getSharedApiAuthHeaders so credentials are included.
        const config = getApiConfig();
        const url = `${config.baseUrl}${this.basePath}/bulk-delete/`;
        const res = await fetch(url, {
            method: 'DELETE',
            headers: {
                ...getSharedApiAuthHeaders(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ ids }),
        });
        const json = (await res.json().catch(() => null)) as {
            success?: boolean;
            code?: number;
            message?: string;
            description?: string;
            data?: { deleted: number; not_found: string[] };
        } | null;
        if (!res.ok) {
            // EAA-1770: Preserve envelope description so FE toasts show the
            // translated "content in use" message instead of a generic failure.
            const description = (json?.description || json?.message || '').trim();
            throw {
                message: description || `bulkDeleteContents failed (${res.status})`,
                description: json?.description,
                status: res.status,
                code: json?.message,
                errorCode: json?.code,
            };
        }
        return json?.data ?? (json as unknown as { deleted: number; not_found: string[] });
    }

    /**
     * Get content by meta_id
     * @param metaId - The meta_id of the content (format: content-v{version}+{tenant}+{source}@{library}@{type_id}+{content_id})
     * @param options.bypassCache - Skip the browser HTTP cache for this request.
     *   EAA-1740: this endpoint's BE view sends `Cache-Control: max-age=3600`
     *   (content/views.py ContentByMetaIdView) — fine for the general
     *   published-content player, but metaId is stable across content
     *   versions, so after an AI regenerate the browser can keep serving the
     *   pre-regenerate response for up to an hour with no network round-trip
     *   at all. Callers that must see the latest version right after a save
     *   (e.g. the AI-gen preview iframe) should pass bypassCache: true.
     * @returns Content with all data including content_type.library, content_type.source, etc.
     */
    async getContentByMetaId(metaId: string, options?: { bypassCache?: boolean }): Promise<Content> {
        const response = await sharedApiClient.get<Content>(
            `${this.basePath}/meta/${encodeURIComponent(metaId)}`,
            options?.bypassCache ? { cache: 'no-store' } : undefined
        );
        return response.data;
    }

    /**
     * Get content by unique code
     * @param code - The unique code of the content (format: ACH_{tenant}_{yymmdd}_{index}, e.g., ACH_EMG_260126_01)
     * @returns Content with all data including content_type.library, content_type.source, etc.
     */
    async getContentByCode(code: string): Promise<Content> {
        const response = await sharedApiClient.get<Content>(
            `${this.basePath}/code/${encodeURIComponent(code)}`
        );
        return response.data;
    }

    /**
     * Grade essay content via AI scoring API
     * Uses configured apiBaseUrl (same as other content operations)
     * @param contentId - Content UUID
     * @param request - Essay grading request (user_answer, question_type, language, question_content)
     */
    async listContentVersions(contentId: string): Promise<ContentVersionItem[]> {
        const response = await sharedApiClient.get<ContentVersionItem[]>(
            `${this.basePath}/${contentId}/versions/`
        );
        return response.data;
    }

    /**
     * Start rollback job. Returns job_id (202 Accepted); poll job status for completion.
     */
    async rollbackContentVersion(contentId: string, version: number): Promise<RollbackResponse> {
        const response = await sharedApiClient.post<RollbackResponse>(
            `${this.basePath}/${contentId}/rollback`,
            { version }
        );
        return response.data;
    }

    async gradeEssay(contentId: string, request: EssayGradeRequest): Promise<EssayGradeResult> {
        const response = await sharedApiClient.post<EssayGradeResult>(
            `${this.basePath}/${contentId}/essay/grade`,
            request,
            // AI scoring proxy: backend allows up to 60s; give extra headroom
            { timeoutMs: 90_000 }
        );
        return response.data;
    }

    /**
     * Grade speaking content via AI scoring API (multipart proxy to agent /api/v3/scoring/speaking).
     */
    async gradeSpeaking(
        contentId: string,
        payload: SpeakingGradePayload,
        options?: PostFormDataOptions
    ): Promise<SpeakingGradeResult> {
        const form = buildSpeakingGradeFormData(payload);
        const response = await sharedApiClient.postFormData<SpeakingGradeResult>(
            `${this.basePath}/${contentId}/speaking/grade`,
            form,
            options
        );
        return response.data;
    }

    /**
     * Start a background AI essay grading job (202 Accepted).
     * Poll GET /integrations/jobs/{job_id} for the result.
     *
     * When `sessionToken` is provided (multi-player mode), uses a raw fetch with
     * `Authorization: Session <token>` instead of the global sharedApiClient auth.
     * This avoids overwriting the view-token used for content reads.
     */
    async gradeEssayJob(
        contentId: string,
        request: EssayGradeRequest,
        sessionToken?: string,
    ): Promise<{ job_id: string; status: string }> {
        if (sessionToken) {
            // WHY: In multi-player mode, the global API client is configured with a
            // view-token (Bearer) for content reads. Essay grading requires
            // SessionTokenAuthentication. Use raw fetch to avoid auth collision.
            const config = getApiConfig();
            const url = `${config.baseUrl}${this.basePath}/${contentId}/essay/grade-job`;
            const res = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Session ${sessionToken}`,
                },
                body: JSON.stringify(request),
            });
            const json = (await res.json().catch(() => null)) as {
                data?: { job_id: string; status: string };
                message?: string;
            } | null;
            if (!res.ok) {
                throw new Error(json?.message || `gradeEssayJob failed (${res.status})`);
            }
            return json?.data ?? (json as unknown as { job_id: string; status: string });
        }
        const response = await sharedApiClient.post<{ job_id: string; status: string }>(
            `${this.basePath}/${contentId}/essay/grade-job`,
            request
        );
        return response.data;
    }

    /**
     * Start a background AI speaking grading job (202 Accepted).
     * Poll GET /integrations/jobs/{job_id} for the result.
     */
    async gradeSpeakingJob(
        contentId: string,
        payload: SpeakingGradePayload,
        options?: PostFormDataOptions,
        sessionToken?: string
    ): Promise<{ job_id: string; status: string }> {
        const form = buildSpeakingGradeFormData(payload);
        if (sessionToken) {
            // ELMS-2365: multi-player mode — sharedApiClient holds the view-token, so the job
            // would carry no session id and ACH could not write the score back to the
            // session after the LMS closes it. Raw multipart fetch with Session auth (same
            // reason as gradeEssayJob). No Content-Type: the browser sets the boundary.
            const config = getApiConfig();
            const res = await fetch(`${config.baseUrl}${this.basePath}/${contentId}/speaking/grade-job`, {
                method: 'POST',
                headers: { Authorization: `Session ${sessionToken}` },
                body: form,
                signal: options?.signal,
            });
            const json = (await res.json().catch(() => null)) as {
                data?: { job_id: string; status: string };
                message?: string;
            } | null;
            if (!res.ok) {
                throw new Error(json?.message || `gradeSpeakingJob failed (${res.status})`);
            }
            return json?.data ?? (json as unknown as { job_id: string; status: string });
        }
        const response = await sharedApiClient.postFormData<{ job_id: string; status: string }>(
            `${this.basePath}/${contentId}/speaking/grade-job`,
            form,
            options
        );
        return response.data;
    }

    /**
     * EAA-2117: start an AI grading job for UNSAVED content (202 Accepted).
     * `POST /content/draft/grade-job` takes the same per-type fields as
     * `/content/<id>/<type>/grade-job` plus `content_type` and, when known, `tenant_id`.
     * Essay posts JSON; speaking posts multipart. Authors only — the BE rejects learner
     * session tokens here, so this never sends one.
     */
    async gradeDraftJob(
        contentType: 'essay',
        payload: EssayGradeRequest,
        tenantId?: string
    ): Promise<GradeJobStart>;
    async gradeDraftJob(
        contentType: 'speaking',
        payload: SpeakingGradePayload,
        tenantId?: string,
        options?: PostFormDataOptions
    ): Promise<GradeJobStart>;
    async gradeDraftJob(
        contentType: DraftGradeContentType,
        payload: EssayGradeRequest | SpeakingGradePayload,
        tenantId?: string,
        options?: PostFormDataOptions
    ): Promise<GradeJobStart> {
        const path = `${this.basePath}/draft/grade-job`;
        if (contentType === 'speaking') {
            const form = buildSpeakingGradeFormData(payload as SpeakingGradePayload);
            form.append('content_type', contentType);
            if (tenantId) {
                form.append('tenant_id', tenantId);
            }
            const response = await sharedApiClient.postFormData<GradeJobStart>(path, form, options);
            return response.data;
        }
        const body = {
            ...(payload as EssayGradeRequest),
            content_type: contentType,
            ...(tenantId ? { tenant_id: tenantId } : {}),
        };
        const response = await sharedApiClient.post<GradeJobStart>(path, body);
        return response.data;
    }

    /**
     * EAA-2117: start an essay grade-job — saved content (UUID) goes to
     * `/content/<id>/essay/grade-job` exactly as before; anything else is an unsaved draft
     * and goes to the draft endpoint without the session token.
     */
    async startEssayGradeJob(
        contentId: string,
        request: EssayGradeRequest,
        options: GradeJobDispatchOptions = {}
    ): Promise<GradeJobStartResult> {
        if (isPersistedContentId(contentId)) {
            const job = await this.gradeEssayJob(contentId, request, options.sessionToken);
            return { ...job, draft: false };
        }
        const job = await this.gradeDraftJob('essay', request, options.tenantId);
        return { ...job, draft: true };
    }

    /** EAA-2117: speaking twin of {@link startEssayGradeJob}. */
    async startSpeakingGradeJob(
        contentId: string,
        payload: SpeakingGradePayload,
        options: GradeJobDispatchOptions = {}
    ): Promise<GradeJobStartResult> {
        if (isPersistedContentId(contentId)) {
            const job = await this.gradeSpeakingJob(
                contentId,
                payload,
                options.formOptions,
                options.sessionToken
            );
            return { ...job, draft: false };
        }
        const job = await this.gradeDraftJob(
            'speaking',
            payload,
            options.tenantId,
            options.formOptions
        );
        return { ...job, draft: true };
    }
}

export const contentService = new ContentService();

// Legacy function exports for backward compatibility
export async function fetchContent(contentId: string): Promise<Content> {
    return contentService.getContent(contentId);
}

/**
 * Fetch content by meta_id
 * @param metaId - The meta_id of the content
 * @returns Content with all data including content_type.library, content_type.source, etc.
 */
export async function fetchContentByMetaId(metaId: string): Promise<Content> {
    return contentService.getContentByMetaId(metaId);
}

/**
 * Fetch content by unique code
 * @param code - The unique code of the content (format: ACH_{tenant}_{yymmdd}_{index})
 * @returns Content with all data including content_type.library, content_type.source, etc.
 */
export async function fetchContentByCode(code: string): Promise<Content> {
    return contentService.getContentByCode(code);
}

/**
 * Absolute URL for H5P server routes under /h5p/ (e.g. new/edit, new/play).
 * `configureContentApi` often sets h5pUrl to `{origin}/h5p` (replacing /api); callers must not
 * prepend /h5p again or URLs become .../h5p/h5p/new/edit.
 */
function buildH5pServerUrl(pathUnderH5p: string): string {
    const { h5pUrl } = getApiConfig();
    const base = h5pUrl.replace(/\/+$/, '');
    const path = pathUnderH5p.replace(/^\//, '');
    if (base.endsWith('/h5p')) {
        return `${base}/${path}`;
    }
    return `${base}/h5p/${path}`;
}

/**
 * EAA-1029: read a pre-generated EDITOR model from the static bundle
 * (`apps/h5p-core/src/editors/<Ubername>.json`, written by its `generate.mjs`).
 *
 * Authoring was the last thing still calling the H5P server at runtime, which is
 * a second origin with its own CORS and auth surface — and the source of
 * "Error loading H5P content from server: Failed to fetch" whenever it was
 * unreachable or refused a header. The editor model is the same kind of pure
 * function of the installed library set that the player model is, so it ships
 * static for the same reasons.
 */
async function fetchStaticH5PEditorModel(
    staticBase: string,
    library: string,
    language: 'en' | 'vi'
): Promise<IEditorModel> {
    const ubername = toLibraryUbername(library);
    if (!ubername) {
        throw new Error(`Invalid H5P library name: "${library}" (expected "H5P.Name 1.2")`);
    }
    const base = staticBase.replace(/\/+$/, '');
    // EAA-1029: one subdirectory per language. The model then points its own
    // `staticLibrariesPath` at the matching semantics directory, so the language
    // never has to be threaded through H5P's `getAjaxUrl`.
    const url = `${base}/editors/${language}/${encodeURIComponent(ubername)}.json`;
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) {
        throw new Error(
            `Failed to load static H5P editor model for ${library}: ${response.status} ${response.statusText}`
        );
    }
    const raw = await response.text();
    // Same SPA-fallback trap the player model guards against: a dev server answers
    // 200 with index.html for an unknown path, so "200" alone does not mean the
    // bundle is being served here.
    if (/^\s*</.test(raw)) {
        throw new Error(
            `Static H5P editor model for ${library} returned HTML, not JSON (${url}). ` +
                'The bundle is not being served at this base — copy it there ' +
                '(apps/h5p-core: `install:local`) or fix VITE_H5P_STATIC_BASE.'
        );
    }
    const model = JSON.parse(raw.split('__H5P_BASE__').join(base)) as IEditorModel;
    if (!model.integration) {
        throw new Error(`Static H5P editor model for ${library} has no integration object`);
    }
    // EAA-1928: hand the resolved base to the editor component.
    //
    // Substituting the token in THIS payload is not enough. H5P's own
    // `H5PEditor.loadLibrary` fetches each library's JSON itself (through the
    // `getAjaxUrl('libraries')` override in packages/h5p-webcomponents), and those
    // files carry `__H5P_BASE__` in their own `css`/`javascript` arrays — 117
    // occurrences in editors/vi/H5P.AdvancedBlanks-1.2.json alone. Nothing in our
    // code sees that response, so the token reached the browser verbatim and every
    // editor asset was requested as `/question-bank/__H5P_BASE__/libraries/…` and
    // 404'd. The component needs the base to rewrite those payloads; it cannot
    // derive it from `staticLibrariesPath` without assuming that path's shape.
    (model.integration.editor as { staticBase?: string }).staticBase = base;
    return model;
}

export async function fetchH5PEditorModel(
    library?: string,
    language?: string
): Promise<IEditorModel> {
    if (!library) {
        throw new Error(
            'H5P library is required. Provide library parameter (e.g., H5P.MultiChoice 1.16)'
        );
    }

    // Static bundle first — same order and same reasoning as the player model:
    // the H5P server stays as a fallback so a library that has not been bundled
    // yet still opens in the editor instead of failing outright.
    const { h5pStaticBase } = getApiConfig();
    if (h5pStaticBase) {
        try {
            return await fetchStaticH5PEditorModel(
                h5pStaticBase,
                library,
                normalizeH5PLanguage(language)
            );
        } catch (error) {
            console.warn(
                '[H5P] static editor model unavailable, falling back to H5P server:',
                error instanceof Error ? error.message : error
            );
        }
    }

    const libraryParam = `?library=${encodeURIComponent(library)}`;

    const response = await fetch(buildH5pServerUrl(`new/edit${libraryParam}`), {
        method: 'GET',
        headers: getH5pRequestHeaders(),
    });

    if (!response.ok) {
        throw new Error(
            `Failed to load H5P editor model: ${response.status} ${response.statusText}`
        );
    }

    return response.json();
}

/** In-memory cache for H5P player models keyed by `library::language` (scripts, styles, integration) */
const h5pPlayerModelCache = new Map<string, IPlayerModel>();

/**
 * EAA-1029: the static bundle (`apps/h5p-core`, see `src/generate.mjs`) ships
 * `integration.l10n` as `{ en: {...}, vi: {...} }` — one shared H5P-core
 * client-string dictionary per language, since that dictionary is identical
 * across every content type. H5P core itself only ever reads a single
 * `integration.l10n` object, so the manifest's two-language dictionary has to
 * collapse to one before the model reaches the player/editor.
 *
 * Picks the requested language, falls back to `en` when the manifest predates
 * this change (flat, single-language `l10n` — no `en`/`vi` keys) or when the
 * requested language has no dictionary yet, and stamps
 * `metadata.defaultLanguage` to match what was actually picked so the H5P core
 * script (which also reads that field) agrees with the l10n it got.
 */
function selectH5PLanguage(model: IPlayerModel, language: string): IPlayerModel {
    const integration = model.integration as
        | (IPlayerModel['integration'] & { l10n?: Record<string, unknown> })
        | undefined;
    const l10nByLanguage = integration?.l10n;
    const isMultiLanguage =
        l10nByLanguage && ('en' in l10nByLanguage || 'vi' in l10nByLanguage);
    if (!integration || !isMultiLanguage) {
        return model;
    }

    const picked = (l10nByLanguage[language] ?? l10nByLanguage.en) as Record<string, unknown>;
    const resolvedLanguage = language in l10nByLanguage ? language : 'en';
    integration.l10n = picked;

    const contents = (integration as { contents?: Record<string, { metadata?: { defaultLanguage?: string } }> })
        .contents;
    for (const entry of Object.values(contents ?? {})) {
        if (entry?.metadata) {
            entry.metadata.defaultLanguage = resolvedLanguage;
        }
    }

    return model;
}

/** Normalize an i18next-style locale ('vi-VN', 'en-US') to the two H5P ships translations for. */
function normalizeH5PLanguage(language: string | undefined): 'en' | 'vi' {
    return language?.toLowerCase().startsWith('vi') ? 'vi' : 'en';
}

function deepClonePlayerModel<T>(obj: T): T {
    if (typeof structuredClone === 'function') {
        return structuredClone(obj) as T;
    }
    return JSON.parse(JSON.stringify(obj)) as T;
}

/** `H5P.Blanks 1.14` → `H5P.Blanks-1.14` (manifest file name in the static bundle). */
function toLibraryUbername(library: string): string | null {
    const match = /^(.+)\s+(\d+)\.(\d+)$/.exec(library.trim());
    if (!match) {
        return null;
    }
    return `${match[1]}-${match[2]}.${match[3]}`;
}

/**
 * Read a pre-generated player model from the static H5P bundle
 * (`apps/h5p-core`, see its `src/generate.mjs`).
 *
 * The bundle stores every absolute URL as `__H5P_BASE__/...` so one bundle can be
 * served from any base (same-origin path, S3, CDN) without regeneration; the token
 * is substituted here. `user` is not in the bundle (it is per-request on the H5P
 * server) — H5P core only needs it to exist, and the embed runs anonymously.
 */
async function fetchStaticH5PPlayerModel(
    staticBase: string,
    library: string
): Promise<IPlayerModel> {
    const ubername = toLibraryUbername(library);
    if (!ubername) {
        throw new Error(`Invalid H5P library name: "${library}" (expected "H5P.Name 1.2")`);
    }
    const base = staticBase.replace(/\/+$/, '');
    const url = `${base}/manifests/${encodeURIComponent(ubername)}.json`;
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) {
        throw new Error(
            `Failed to load static H5P manifest for ${library}: ${response.status} ${response.statusText}`
        );
    }
    const raw = await response.text();
    // A dev server's SPA fallback answers 200 with index.html for a path it does
    // not recognise, so "200" alone does not mean the bundle is there. Say so
    // explicitly: without this the caller only saw a JSON.parse error and silently
    // fell back to the H5P server — the very dependency the bundle removes.
    if (/^\s*</.test(raw)) {
        throw new Error(
            `Static H5P manifest for ${library} returned HTML, not JSON (${url}). ` +
                'The bundle is not being served at this base — copy it there ' +
                '(apps/h5p-core: `install:local`) or fix VITE_H5P_STATIC_BASE.'
        );
    }
    const model = JSON.parse(raw.split('__H5P_BASE__').join(base)) as IPlayerModel;
    if (!model.integration) {
        throw new Error(`Static H5P manifest for ${library} has no integration object`);
    }
    model.integration.user = model.integration.user ?? {
        name: 'Anonymous',
        mail: '',
        id: 'anonymous',
    };
    return model;
}

/**
 * @param language i18next-style locale of the active app UI ('vi', 'vi-VN',
 * 'en', …), normalized to the language H5P core chrome (Fullscreen, Check,
 * Retry, Show solution, …) renders in. Defaults to English so existing
 * callers that pass nothing behave exactly as before. See `selectH5PLanguage`
 * — the live H5P server fallback below only ever returns English, so this
 * only changes anything when the static bundle serves the request.
 */
export async function fetchH5PPlayerModel(
    library?: string,
    language?: string
): Promise<IPlayerModel> {
    if (!library) {
        throw new Error(
            'H5P library is required. Provide library parameter (e.g., H5P.MultiChoice 1.16)'
        );
    }
    const resolvedLanguage = normalizeH5PLanguage(language);
    const cacheKey = `${library}::${resolvedLanguage}`;

    const cached = h5pPlayerModelCache.get(cacheKey);
    if (cached) {
        return deepClonePlayerModel(cached);
    }

    // Static bundle first: it removes the runtime dependency on the H5P server
    // (and its separate domain). The server stays as a fallback so a library that
    // has not been bundled yet still plays instead of failing outright.
    const { h5pStaticBase } = getApiConfig();
    if (h5pStaticBase) {
        try {
            const staticModel = await fetchStaticH5PPlayerModel(h5pStaticBase, library);
            selectH5PLanguage(staticModel, resolvedLanguage);
            h5pPlayerModelCache.set(cacheKey, staticModel);
            return deepClonePlayerModel(staticModel);
        } catch (error) {
            console.warn(
                '[H5P] static player model unavailable, falling back to H5P server:',
                error instanceof Error ? error.message : error
            );
        }
    }

    const libraryParam = `?library=${encodeURIComponent(library)}`;

    const response = await fetch(buildH5pServerUrl(`new/play${libraryParam}`), {
        method: 'GET',
        headers: getH5pRequestHeaders(),
    });

    if (!response.ok) {
        throw new Error(
            `Failed to load H5P player model: ${response.status} ${response.statusText}`
        );
    }

    // EAA-1029: the live H5P server (packages/h5p-server) only ever answers in
    // English here — nothing plumbs `language` through this endpoint yet, so
    // there is nothing to select. Vietnamese H5P chrome ships through the
    // static bundle above; this path is the pre-bundle fallback.
    const playerModel = (await response.json()) as IPlayerModel;
    h5pPlayerModelCache.set(cacheKey, playerModel);
    return deepClonePlayerModel(playerModel);
}

export function extractH5PContentFromData(data: Record<string, unknown>): H5PContentData | null {
    // Check if h5p data exists in nested format
    const h5pData = data.h5p as H5PContentData | undefined;
    if (h5pData?.library && h5pData?.params && h5pData?.metadata) {
        return h5pData;
    }

    // Check if h5p data is at root level
    if (data.library && data.params && data.metadata) {
        return {
            library: data.library as string,
            params: data.params,
            metadata: data.metadata as IContentMetadata,
        };
    }

    return null;
}

/**
 * Count gradable answer units from ACH content data based on the content-type slug.
 *
 * Delegates to `countQuestions` from `@ai-content-hub/content-core` which is
 * the single source of truth for this logic.
 *
 * @param slug  Content-type slug (e.g. 'mcq-set', 'question-set', 'quiz')
 * @param data  The content data JSON
 * @returns     Number of gradable answer units
 */
export function countSubQuestions(
    slug: string,
    data: Record<string, unknown> | null | undefined,
): number {
    return countQuestions(slug, data);
}

export default contentService;
